/* eslint-disable react-hooks/exhaustive-deps */

import Text from "@components/CustomText";
import SearchIcon from "@assets/svg/SearchIcon";
import {
  getDbTouchpoints,
  assignTouchpointBulk,
  updateDBRoute,
} from "@services/api";
import React, { memo, useCallback, useRef, useState, useMemo } from "react";
import {
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ScrollView,
  FlatList,
  ActivityIndicator,
  Modal,
  TouchableOpacity,
  Linking,
  Dimensions,
} from "react-native";
import _, { debounce } from "lodash";
import color from "@constants/color";
import Button from "@components/Button";
import DownArrowSmall from "@assets/svg/DownArrowSmall";
import CallIcon from "@assets/svg/newCall";
import DbScooter from "@assets/svg/CpDeliveryBoys/db_scooter";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import { CP_DB_EVENTS } from "@constants/analyticsEventsCT";
import HeaderWithSearch from "@components/SearchHeader/HeaderWithSearch";
import t from "@constants/localization";
import { useFocusEffect } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import { actions as configActions } from "@reducers/configReducer";
import { actions } from "@reducers/cpDeliveryRoutesReducer";
import SwitchDbConfirmationModal from "./SwitchDbConfirmationModal";
import getDbsForFilters from "./getDbsForFilters";

const ALL_FILTER = {
  route_id: "ALL",
  route_name: "ALL",
  db_name: "ALL",
  db_id: "",
};

const CpDeliveryRoutes = ({ navigation, route }) => {
  // Performance tracking - component mount
  const startTime = Date.now();
  console.log('PERFORMANCE: COMPONENT_RENDER_TOTAL started');
  
  const dispatch = useDispatch();
  
  // Use selector to get state from the reducer
  const {
    routes,
    filteredRoutes,
    selectedRouteId,
    switchDbId,
    loading,
    draftRoutes,
    expandedState,
    showSwitchDbConfirmModal,
    isCurrentDeliveryTab,
    isSearchEnabled,
    error
  } = useSelector(state => state.cpDeliveryRoutesReducer);

  const isProcessingRef = useRef(false);
  const prevRoutesRef = useRef(null);

  const PATH = "screens/CpDeliveryRoutes/index";

  // Memoize allRoutes calculation to prevent recalculation on every render
  const allRoutes = useMemo(() => {
    const calcRoutesStart = Date.now();
    const initialRoutes = [ALL_FILTER].concat(filteredRoutes?.active_routes || []);
    const result = getDbsForFilters(initialRoutes, routes?.assigned_routes || []);
    console.log(`PERFORMANCE: CALCULATE_ROUTES completed in ${Date.now() - calcRoutesStart}ms`);
    return result;
  }, [filteredRoutes?.active_routes, routes?.assigned_routes]);

  // Memoize the debounced search function to prevent recreation on every render
  const onSearchInputChange = useCallback(
    debounce((text) => {
      dispatch(actions.filterRoutesBySearch(text, routes));
    }, 500),
    [routes, dispatch]
  );

  // Handler for toggling search enabled state
  const setIsSearchEnabled = useCallback((value) => {
    dispatch(actions.toggleSearchEnabled(value));
  }, [dispatch]);

  // Memoize the filterRoutes function to prevent recreation on every render
  const filterRoutes = useCallback((data, searchQuery) => {
    if (!data || !searchQuery) return data;
    
    const searchLower = searchQuery.toLowerCase();
    
    const filteredData = {
      active_routes: data?.active_routes,
      assigned_routes: data?.assigned_routes,
      misroute_count: data?.misroute_count,
      dbs_to_switch: data?.dbs_to_switch,
      unassigned_count: data?.unassigned_count,
      touchpoints: data?.touchpoints?.filter((r) => {
        return (
          r?.cx_address?.toLowerCase().includes(searchLower) ||
          r?.cx_name?.toLowerCase().includes(searchLower) ||
          r?.cx_phone_number?.includes(searchQuery)
        );
      }),
    };
    return filteredData;
  }, []);

  useFocusEffect(
    useCallback(() => {
      dispatch(actions.fetchData("ALL", isCurrentDeliveryTab));
    }, [isCurrentDeliveryTab, dispatch]),
  );

  // This function is kept for backward compatibility with existing code
  // It now dispatches the action instead of directly updating state
  const fetchData = async (routeId) => {
    const apiCallStart = Date.now();
    console.log('PERFORMANCE: API_CALL_TOTAL started');
    
    dispatch(actions.fetchData(routeId || selectedRouteId, isCurrentDeliveryTab));
    
    // The following code is handled by the saga, but we keep the logging for performance tracking
    console.log(`PERFORMANCE: API_CALL_TOTAL completed in ${Date.now() - apiCallStart}ms`);
  };

  const onPressSelectRoute = useCallback((routeId) => {
    dispatch(actions.selectRoute(routeId));
    dispatch(actions.fetchData(routeId, isCurrentDeliveryTab));
  }, [dispatch, isCurrentDeliveryTab]);

  const onPressRoutePill = useCallback(({ routeId, routeData }) => {
    const {
      delivery_date,
      cx_id,
      cx_address,
      cx_lat,
      cx_lng,
      order_id,
      order_edd,
    } = routeData;

    const key = `${delivery_date}_${cx_id}`;
    const newDraftRoutes = { ...draftRoutes };
    
    if (newDraftRoutes[key] && newDraftRoutes[key].route_id === routeId) {
      delete newDraftRoutes[key];
    } else {
      newDraftRoutes[key] = {
        route_id: routeId,
        delivery_date,
        cx_id,
        cx_address,
        cx_lat,
        cx_lng,
        order_id,
        order_edd,
      };
    }
    
    dispatch(actions.updateDraftRoutes(newDraftRoutes));
  }, [draftRoutes, dispatch]);

  const onPressAssignCpTpsBulk = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    try {
      if (draftRoutes && Object.keys(draftRoutes).length > 0) {
        const data = Object.values(draftRoutes);
        dispatch(actions.assignTouchpointBulk(data, isCurrentDeliveryTab));
      }
    } catch (error) {
      console.log(error);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const onPressExpandSwitchDb = () => {
    dispatch(actions.toggleExpandState());
  };

  const onPressSwitchDb = (dbID) => {
    dispatch(actions.setSwitchDbId(dbID));
    dispatch(actions.toggleSwitchDbConfirmModal(true));
  };

  const onCloseSwitchDbModal = () => {
    dispatch(actions.setSwitchDbId(null));
    dispatch(actions.toggleSwitchDbConfirmModal(false));
  };

  const transferAllDeliveries = async () => {
    if (switchDbId) {
      const payload = {
        db_id: switchDbId,
        route_id: filteredRoutes?.route_id,
      };

      dispatch(actions.updateDbRoute(payload));
    }
  };

  const isRouteHasDb = (routeId) => {
    const currentRoute = routes?.assigned_routes?.find(
      (r) => r.route_id === routeId,
    );
    return currentRoute?.db_id || false;
  };

  const callCustomer = (phoneNumber) => {
    handleCallButtonPress(phoneNumber, CP_DB_EVENTS.CUSTOMERS_CALLED);
  };

  const changeTab = (tab) => {
    dispatch(actions.setCurrentDeliveryTab(tab));
    dispatch(actions.fetchData(selectedRouteId, tab));
  };

  // Optimize renderRoute with proper dependency array
  const renderRoute = useCallback(
    (routeData, key) => {
      const renderRouteStart = Date.now();
      const result = (
      <RouteContainer
        key={key}
        routeData={routeData}
        activeRoutes={filteredRoutes?.active_routes || []}
        draftRoutes={draftRoutes}
        selectedRouteId={selectedRouteId}
        onPressRoutePill={onPressRoutePill}
        callCustomer={callCustomer}
      />
    );
      return result;
    },
    [filteredRoutes?.active_routes, draftRoutes, selectedRouteId, onPressRoutePill, callCustomer],
  );

  // RouteContainer is defined later in the file

  return (
    <>
      <HeaderWithSearch
        headertitle={t(`${PATH}/headerTitle`)}
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
        isSearchEnabled={isSearchEnabled}
        onSearchInputChange={onSearchInputChange}
        onPressCrossIcon={() => {
          setIsSearchEnabled(false);
          onSearchInputChange("");
        }}
        searchPlaceholder={t(`${PATH}/searchPlaceholder`)}
        rightComponent={
          <View style={{ flexDirection: "row" }}>
            <TouchableOpacity
              testID="search_enable"
              onPress={() => setIsSearchEnabled(true)}
            >
              <SearchIcon height={20} width={20} fill={color.black} />
            </TouchableOpacity>
          </View>
        }
      />
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          backgroundColor: color.white,
          alignItems: "center",
          elevation: 0,
        }}
      >
        <TabButton
          title={t(`${PATH}/currentDeliveries`)}
          isActive={isCurrentDeliveryTab}
          onPress={() => changeTab(true)}
        />
        <TabButton
          title={t(`${PATH}/upcomingDeliveries`)}
          isActive={!isCurrentDeliveryTab}
          onPress={() => changeTab(false)}
        />
      </View>

      <>
        <View>
          <FlatList
            horizontal
            data={allRoutes}
            keyExtractor={(item, index) => `route-${item.route_id || index}`}
            style={styles.filtersContainer}
            showsHorizontalScrollIndicator={false}
            renderItem={({ item, index }) => (
              <TouchableWithoutFeedback
                onPress={() => onPressSelectRoute(item.route_id)}
              >
                <View
                  style={[
                    selectedRouteId === item?.route_id
                      ? styles.selectedFilter
                      : styles.filter,
                    { marginLeft: item?.route_id === "ALL" ? 16 : 4 },
                  ]}
                >
                  <View
                    style={{
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "flex-start",
                      maxWidth: 100,
                      marginRight: item?.route_id === "ALL" ? 0 : 15,
                    }}
                  >
                    <Text
                      variant="body_text_3"
                      numberOfLines={1}
                      style={
                        selectedRouteId === item?.route_id
                          ? styles.selectedText
                          : styles.notSelectedText
                      }
                    >
                      {`${item?.route_name || ""}`}
                    </Text>
                    {item?.route_id !== "ALL" &&
                      item?.route_id !== "MISROUTE" &&
                      item?.route_id !== "UNASSIGN" && (
                        <Text
                          variant="caption_3"
                          numberOfLines={1}
                          style={[
                            item?.db_id === null
                              ? { color: color.plain_red }
                              : selectedRouteId === item?.route_id
                                ? { color: "rgba(255, 255, 255, 0.56)" }
                                : { color: color.mediumBlacklightBlack },
                            { fontWeight: "400" },
                          ]}
                        >
                          {`${item?.db_name || "No Delivery Boy"}`}
                        </Text>
                      )}
                  </View>
                  {item?.route_id !== "ALL" && (
                    <Text
                      variant="title_16"
                      style={
                        selectedRouteId === item?.route_id
                          ? styles.selectedText
                          : styles.notSelectedText
                      }
                    >
                      {`${item?.customer_count || 0}`}
                    </Text>
                  )}
                </View>
              </TouchableWithoutFeedback>
            )}
          />
        </View>
        {filteredRoutes.touchpoints?.length === 0 ? (
          <View
            style={{
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <DbScooter />
            <Text
              variant="body_text_2"
              style={{ color: color.mediumBlacklightBlack, padding: 20 }}
            >
              {isSearchEnabled
                ? t(`${PATH}/noCustomerText`)
                : selectedRouteId === "MISROUTE"
                  ? t(`${PATH}/noMisrouteAssigned`)
                  : selectedRouteId === "UNASSIGN"
                    ? t(`${PATH}/noUnassignedAssigned`)
                    : t(`${PATH}/noCustomerAssigned`)}
            </Text>
          </View>
        ) : (
          <>
            <FlatList
              showsVerticalScrollIndicator={false}
              style={{ height: "100%" }}
              data={filteredRoutes.touchpoints || []}
              keyExtractor={(item, index) => `touchpoint-${item.cx_id || index}`}
              renderItem={({ item, index }) => renderRoute(item, `touchpoint-${item.cx_id || index}`)}
              ListHeaderComponent={() => (
                selectedRouteId !== "ALL" &&
                selectedRouteId !== "UNASSIGN" &&
                selectedRouteId !== "MISROUTE" &&
                filteredRoutes.touchpoints?.length !== 0 ? (
                  <View style={styles.switchDbContainer}>
                    <TouchableWithoutFeedback onPress={onPressExpandSwitchDb}>
                      <View style={styles.switchDbRow}>
                        <Text
                          variant="body_text_2"
                          style={{ color: "rgba(228, 0, 0, 1)" }}
                        >
                          {t(`${PATH}/dbTransfer`)}
                        </Text>
                        <View
                          style={{
                            height: 24,
                            width: 24,
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <DownArrowSmall />
                        </View>
                      </View>
                    </TouchableWithoutFeedback>
                    {expandedState ? (
                      <View style={styles.switchDbPills}>
                        {filteredRoutes?.dbs_to_switch?.map((item, index) =>
                          item?.id === "UNASSIGN" &&
                          !isRouteHasDb(selectedRouteId) ? null : (
                            <TouchableWithoutFeedback
                              key={`db-switch-${item.id || index}`}
                              onPress={() => onPressSwitchDb(item.id)}
                            >
                              <View style={styles.notSelectedPill}>
                                <Text
                                  numberOfLines={1}
                                  variant="body_text_3"
                                  style={styles.notSelectedText}
                                >
                                  {item?.db_name || ""}
                                </Text>
                              </View>
                            </TouchableWithoutFeedback>
                          ),
                        ) || null}
                      </View>
                    ) : (
                      <View style={{ height: 0 }} />
                    )}
                  </View>
                ) : null
              )}
              initialNumToRender={10}
              maxToRenderPerBatch={10}
              windowSize={10}
              onEndReachedThreshold={0.5}
            />
            <View
              style={{
                padding: 10,
                backgroundColor: "#fff",
                elevation: 10,
              }}
            >
              <Button
                btnText={
                  Object.keys(draftRoutes).length === 0
                    ? t(`${PATH}/buttonText`)
                    : `${t(`${PATH}/buttonText`)} ` +
                      `(${Object.keys(draftRoutes).length})`
                }
                onPress={onPressAssignCpTpsBulk}
                buttonWidth="100%"
                buttonColor={color.plain_green}
                buttonStyle={{ borderRadius: 10 }}
                disabled={
                  Object.keys(draftRoutes).length === 0 ||
                  isProcessingRef.current
                }
              />
            </View>
            <SwitchDbConfirmationModal
              visible={showSwitchDbConfirmModal}
              dbs={filteredRoutes.dbs_to_switch}
              selectedDbId={selectedRouteId}
              switchDbId={switchDbId}
              onCloseSwitchDbModal={onCloseSwitchDbModal}
              transferAllDeliveries={transferAllDeliveries}
            />
          </>
        )}
        <Modal
          statusBarTranslucent
          navigationBarTranslucent
          visible={loading}
          transparent
        >
          <View
            style={{
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(0, 0, 0, 0.4)",
            }}
          >
            <ActivityIndicator size="large" color={color.primary} />
          </View>
        </Modal>
      </>
    </>
  );
};

const getPillStyles = ({ routeData, draftRoutes, item }) => {
  const { delivery_date, cx_id, route_id } = routeData;
  const isDraftPill =
    (draftRoutes[`${delivery_date}_${cx_id}`] &&
      draftRoutes[`${delivery_date}_${cx_id}`].route_id === item.route_id) ||
    false;
  const isDraftMisRoutePill =
    (draftRoutes[`${delivery_date}_${cx_id}`] &&
      draftRoutes[`${delivery_date}_${cx_id}`].route_id === "MISROUTE") ||
    false;
  if (item.route_id === route_id && route_id === "MISROUTE")
    return styles.seletedPillMisRoute;
  if (item.route_id === route_id) return styles.selectedPill;
  if (isDraftPill && isDraftMisRoutePill) return styles.draftPillMisRoute;
  if (isDraftPill) return styles.draftPill;
  return styles.notSelectedPill;
};
const getPillTextStyles = ({ routeData, draftRoutes, item }) => {
  const { delivery_date, cx_id, route_id } = routeData;
  const isDraftPill =
    (draftRoutes[`${delivery_date}_${cx_id}`] &&
      draftRoutes[`${delivery_date}_${cx_id}`].route_id === item.route_id) ||
    false;
  const isDraftMisRoutePill =
    (draftRoutes[`${delivery_date}_${cx_id}`] &&
      draftRoutes[`${delivery_date}_${cx_id}`].route_id === "MISROUTE") ||
    false;
  if (item.route_id === route_id) return styles.selectedText;
  if (isDraftPill && isDraftMisRoutePill)
    return styles.draftSelectedTextMisRoute;
  if (isDraftPill) return styles.draftSelectedText;
  return styles.notSelectedText;
};

// Optimize RouteContainer with proper prop comparison
const RouteContainer = memo(
  ({
    routeData,
    activeRoutes,
    draftRoutes,
    selectedRouteId,
    onPressRoutePill,
    callCustomer,
  }) => {
    const routeContainerStart = Date.now();
    const isDraft =
      !!draftRoutes[`${routeData.delivery_date}_${routeData.cx_id}`];
    const routeStyle = isDraft
      ? styles.draftRouteContainer
      : styles.routeContainer;
    const openMapHandler = (lat, lng) => async () => {
      const url = `google.navigation:q=${lat},${lng}`;
      Linking.canOpenURL(url).then((supported) => {
        if (supported) return Linking.openURL(url);
        else {
          const browser_url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
          return Linking.openURL(browser_url);
        }
      });
    };
    const result = (
      <View style={routeStyle}>
        {routeData?.delay_text && (
          <Tag text={routeData?.delay_text} bgcolor="rgba(252, 166, 17, 1)" />
        )}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            paddingVertical: 4,
            paddingBottom: 8,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Text
              variant="body_text_2"
              style={{
                maxWidth: Dimensions.get("window").width * 0.6,
              }}
            >
              {routeData.cx_name}
            </Text>
            {routeData?.is_priority_cx && (
              <View style={{ marginLeft: 6 }}>
                <Tag text="Priority" bgcolor="rgba(66, 116, 234, 1)" />
              </View>
            )}
          </View>

          <TouchableWithoutFeedback
            onPress={() => callCustomer(routeData.cx_phone_number)}
          >
            <CallIcon strokeColor="#2195F2" width={18} height={18} />
          </TouchableWithoutFeedback>
        </View>
        <TouchableOpacity
          testID="cpdr-open-map"
          disabled={!(routeData?.cx_lat && routeData?.cx_lng)}
          onPress={openMapHandler(routeData?.cx_lat, routeData?.cx_lng)}
        >
          <Text
            variant="body_text_3"
            style={[
              styles.addressText,
              routeData?.cx_lat &&
                routeData?.cx_lng && {
                  color: "#4274EA",
                  textDecorationLine: "underline",
                },
            ]}
          >
            {routeData.cx_address}
          </Text>
        </TouchableOpacity>

        <RouteList
          routeData={routeData}
          draftRoutes={draftRoutes}
          routesList={activeRoutes}
          onPressRoutePill={onPressRoutePill}
        />
      </View>
    );
    return result;
  },
);

const Tag = ({ text, bgcolor }) => (
  <View
    style={{
      backgroundColor: bgcolor,
      paddingHorizontal: 6,
      paddingVertical: 4,
      borderRadius: 40,
      alignSelf: "flex-start",
      top: -2,
    }}
  >
    <Text variant="caption_3" style={{ color: "#fff", fontWeight: "bold" }}>
      {text}
    </Text>
  </View>
);

// Optimize RouteList with proper prop comparison
const RouteList = memo(
  ({ routeData, draftRoutes, routesList, onPressRoutePill }) => {
    const result = (
    <FlatList
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.dbListContainer}
      data={[1]}
      keyExtractor={() => 'route-list'}
      renderItem={() => (
      <View style={{ flexDirection: "column" }}>
        {routesList?.length > 6 ? (
          <>
            <View style={{ flexDirection: "row" }}>
              {getRouteListSorted(routesList, routeData, 1).map(
                (item, index) => {
                  const pillStyle = getPillStyles({
                    routeData,
                    draftRoutes,
                    item,
                  });
                  const pillTextStyle = getPillTextStyles({
                    routeData,
                    draftRoutes,
                    item,
                  });
                  return (
                    <TouchableWithoutFeedback key={`route-top-${item.route_id}-${index}`}
                      onPress={() =>
                        onPressRoutePill({ routeId: item.route_id, routeData })
                      }
                    >
                      <View key={index} style={pillStyle}>
                        <Text variant="body_text_3" style={pillTextStyle}>
                          {item.route_name}
                        </Text>
                      </View>
                    </TouchableWithoutFeedback>
                  );
                },
              )}
            </View>
            <View style={{ flexDirection: "row" }}>
              {getRouteListSorted(routesList, routeData, 2).map(
                (item, index) => {
                  const pillStyle = getPillStyles({
                    routeData,
                    draftRoutes,
                    item,
                  });
                  const pillTextStyle = getPillTextStyles({
                    routeData,
                    draftRoutes,
                    item,
                  });
                  return (
                    <TouchableWithoutFeedback key={`route-bottom-${item.route_id}-${index}`}
                      onPress={() =>
                        onPressRoutePill({ routeId: item.route_id, routeData })
                      }
                    >
                      <View key={index} style={pillStyle}>
                        <Text variant="body_text_3" style={pillTextStyle}>
                          {item.route_name}
                        </Text>
                      </View>
                    </TouchableWithoutFeedback>
                  );
                },
              )}
            </View>
          </>
        ) : (
          <View style={{ flexDirection: "row" }}>
            {getRouteListSorted(routesList, routeData, 1).map((item, index) => {
              const pillStyle = getPillStyles({ routeData, draftRoutes, item });
              const pillTextStyle = getPillTextStyles({
                routeData,
                draftRoutes,
                item,
              });
              return (
                <TouchableWithoutFeedback
                  key={`pill-single-${item.route_id}-${index}`}
                  onPress={() =>
                    onPressRoutePill({ routeId: item.route_id, routeData })
                  }
                >
                  <View style={pillStyle}>
                    <Text variant="body_text_3" style={pillTextStyle}>
                      {item.route_name}
                    </Text>
                  </View>
                </TouchableWithoutFeedback>
              );
            })}
          </View>
        )}
        <View style={{ flexDirection: "row" }}>
          {getRouteListSorted(routesList, routeData, 3).map((item, index) => {
            const pillStyle = getPillStyles({ routeData, draftRoutes, item });
            const pillTextStyle = getPillTextStyles({
              routeData,
              draftRoutes,
              item,
            });
            return (
              <TouchableWithoutFeedback
                key={`pill-extra-${item.route_id}-${index}`}
                onPress={() =>
                  onPressRoutePill({ routeId: item.route_id, routeData })
                }
              >
                <View style={pillStyle}>
                  <Text variant="body_text_3" style={pillTextStyle}>
                    {item.route_name}
                  </Text>
                </View>
              </TouchableWithoutFeedback>
            );
          })}
        </View>
      </View>
      )}
    />
    );
    return result;
  }
);

const getRouteListSorted = (routesList, routeData, split) => {
  let copyRoutesList = _.cloneDeep(routesList);
  const misrouteAndUnassign = copyRoutesList.filter(
    (route) =>
      route.route_id === "MISROUTE" ||
      (route.route_id === "UNASSIGN" && routeData?.route_id),
  );
  copyRoutesList = copyRoutesList.filter(
    (route) => route.route_id !== "UNASSIGN" && route.route_id !== "MISROUTE",
  );

  const sortedList = copyRoutesList.sort((a, b) => {
    if (a.route_id === routeData.route_id) return -1;
    if (b.route_id === routeData.route_id) return 1;
    if (a.route_id === "UNASSIGN") return 1;
    if (b.route_id === "UNASSIGN") return -1;
    if (a.route_id === "MISROUTE") return 1;
    if (b.route_id === "MISROUTE") return -1;
    return 0;
  });

  if (split === 3) return misrouteAndUnassign;

  if (split === 1 && routesList.length <= 6) return sortedList;

  return split === 1
    ? sortedList.slice(0, Math.ceil(sortedList.length / 2))
    : sortedList.slice(Math.ceil(sortedList.length / 2));
};

const TabButton = ({ title, isActive, onPress }) => {
  return (
    <TouchableOpacity
      testID="sd-sorting-tab-button"
      activeOpacity={0.8}
      style={{
        flex: 1,
        alignItems: "center",
        borderBottomWidth: 4,
        borderBottomColor: isActive ? color.primary : "white",
        paddingVertical: 6,
      }}
      onPress={onPress}
    >
      <Text
        variant="title_16"
        style={{
          paddingVertical: 6,
          color: isActive ? color.black : color.mediumBlacklightBlack,
        }}
      >
        {title}
      </Text>
    </TouchableOpacity>
  );
};

const commonStyles = StyleSheet.create({
  filter: {
    marginVertical: 8,
    marginLeft: 4,
    marginRight: 4,
    padding: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    elevation: 2,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  pill: {
    flexWrap: "wrap",
    marginTop: 6,
    marginRight: 8,
    padding: 4,
    paddingHorizontal: 18,
    borderRadius: 24,
    overflow: "hidden",
  },
  route: {
    margin: 16,
    marginTop: 0,
    padding: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.2,
    shadowRadius: 1.41,
    elevation: 2,
  },
});

const styles = StyleSheet.create({
  filtersContainer: {
    marginTop: 4,
    flexDirection: "row",
    marginRight: 16,
  },
  filter: {
    ...commonStyles.filter,
    backgroundColor: "#fff",
  },
  selectedFilter: {
    ...commonStyles.filter,
    backgroundColor: "rgba(0, 0, 0, 0.86)",
  },
  routeContainer: {
    ...commonStyles.route,
    backgroundColor: "#fff",
    shadowColor: "#000",
  },
  draftRouteContainer: {
    ...commonStyles.route,
    borderWidth: 1,
    borderColor: "rgba(43, 159, 55, 0.2)",
    backgroundColor: "rgba(0, 171, 89, 0.05)",
    shadowColor: "#fff",
  },
  dbListContainer: {
    flexDirection: "row",
    marginBottom: 8,
  },
  notSelectedPill: {
    ...commonStyles.pill,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#e4e4e4",
  },
  selectedPill: {
    ...commonStyles.pill,
    backgroundColor: "#2B9F37",
  },
  seletedPillMisRoute: {
    ...commonStyles.pill,
    backgroundColor: color.primary,
  },
  draftPill: {
    ...commonStyles.pill,
    borderWidth: 0.5,
    borderColor: "rgba(43, 159, 55, 0.2)",
    backgroundColor: "rgba(43, 159, 55, 0.2)",
  },
  draftPillMisRoute: {
    ...commonStyles.pill,
    borderWidth: 0.5,
    borderColor: "rgba(228, 0, 70, 0.1)",
    backgroundColor: "rgba(228, 0, 70, 0.1)",
  },
  notSelectedText: {
    color: "black",
  },
  selectedText: {
    color: "white",
    fontWeight: "700",
  },
  draftSelectedText: {
    color: color.darkGreen,
    fontWeight: "700",
  },
  draftSelectedTextMisRoute: {
    color: color.primary,
    fontWeight: "700",
  },
  addressText: {
    marginBottom: 12,
    color: color.mediumBlacklightBlack,
  },
  switchDbContainer: {
    margin: 16,
    marginVertical: 4,
    marginBottom: 12,
    borderRadius: 12,
    backgroundColor: "#fff",
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "#e4e4e4",
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  switchDbRow: {
    padding: 16,
    paddingVertical: 12,

    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  switchDbPills: {
    paddingTop: 0,
    top: -6,
    padding: 12,
    flexDirection: "row",
    flexWrap: "wrap",
  },
});

export default CpDeliveryRoutes;
